import modal from './modal-v2';
import Tabs from './tabs';

/**
 * Get cookie value.
 *
 * @param {String} name Name of cookie.
 * @return {String|false} Value of cookie or false if cookie is not set.
 */
function getCookieValue(name) {
  const cookies = (document.cookie || '').split('; ');
  let value = false;
  cookies.forEach(cookie => {
    const parts = cookie.split('=');
    if (window.decodeURIComponent(parts[0]) === name) {
      value = parts.length > 1 ? window.decodeURIComponent(parts[1]) : false;
    }
  });

  return value;
}

/**
* Set cookie value.
*
* @param {String} name Cookie to set.
* @param {String} value Cookie value. If empty cookie is removed.
* @param {Object} options Cookie parameters.
* @param {String} [options.path='/'] Cookie path.
* @param {Number} [options.expires=86400] Cookie expiration in seconds.
* @param {String} [options.domain=false] Cookie domain.
* @param {Boolean} [options.secure=true] Whether cookie is only accessible on https protocol.
*/
function setCookieValue(name, value, options) {
  const defaults = {
    path: '/',
    expires: 86400,
    domain: false,
    secure: true
  };
  options = Object.assign(defaults, options || {});

  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + 1000 * options.expires);
    options.expires = date;
  }

  if (!value) {
    options.expires = new Date(0);
  }

  let cookie = window.encodeURIComponent(name) + '=' + (value ? window.encodeURIComponent(value) : '');
  cookie += options.expires ? '; expires=' + options.expires.toUTCString() : '',
  cookie += options.path ? '; path=' + options.path : '',
  cookie += options.domain ? '; domain=' + options.domain : '',
  cookie += options.secure ? '; secure' : ''

  document.cookie = cookie;
}


class CookieDialog {

  constructor(cookieDialogElement) {
    const cookieDialog = modal.createModal(cookieDialogElement);
    const cookiesAcceptedCookie = cookieDialogElement.getAttribute('data-cookie-dialog');
    const answered = getCookieValue(cookiesAcceptedCookie);
    if (answered !== 'true') {
      cookieDialog.show();
    }

    const tablist = cookieDialogElement.querySelector('[role="tablist"]');
    if (tablist) {
      new Tabs(tablist);
    }
    this.dialog = cookieDialog;
    this.element = cookieDialogElement;
    this.acceptedCookie = cookiesAcceptedCookie;

    // Register toggle callbacks for switches
    cookieDialogElement.querySelectorAll('[role="switch"]').forEach(el => {
      el.addEventListener('click', (e) => {
        if (el.getAttribute('aria-readonly') === 'true') {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const toggled = el.getAttribute('aria-checked') === 'false';
        el.setAttribute('aria-checked', toggled ? 'true' : 'false');
      });
    });

    // Bind action buttons to instance methods
    cookieDialogElement.querySelectorAll('[data-cookie-dialog-action]').forEach((el) => {
      el.addEventListener('click', () => {
        const func = this[el.getAttribute('data-cookie-dialog-action')];
        if (typeof func === 'function') {
          func.apply(this);
        }
      });
    });
    document.querySelectorAll('.js-open-cookie-dialog-settings').forEach((el) => {
      el.addEventListener('click', () => {
        cookieDialog.show();
        this.openSettings();
      });
    });
  }

  openSettings() {
    this.element.querySelector('[data-cookie-dialog-view="settings"]').removeAttribute('aria-hidden');
    this.element.querySelector('[data-cookie-dialog-view="info"]').setAttribute('aria-hidden', 'true');
  }

  getCookieOptions() {
    const options = [];
    this.element.querySelectorAll('[data-cookie-dialog-option]').forEach((el) => {
      const optionName = el.getAttribute('data-cookie-dialog-option');
      setCookieValue(optionName, false, { // Set all related cookies to false
        expires: 60 * 60 * 24 * 7 // week
      });
      options.push({ name: optionName, value: el.getAttribute('aria-checked'), sendToGTM: el.getAttribute('data-cookie-send-to-gtm') === 'true' });
    });

    return options;
  }

  save() {
    const options = this.getCookieOptions();
    options.splice(0, 0, { name: this.acceptedCookie, value: 'true', sendToGTM: false });

    // Set cookies
    options.forEach(option => {
      setCookieValue(option.name, option.value, {
        expires: 60 * 60 * 24 * 7 // week
      });
    });

    this.dialog.hide();

    // Send data to gtm
    const gtmData = {
      event: 'cookie_consent_given',
    };
    options.filter(option => option.sendToGTM).forEach(option => {
      gtmData[option.name] = option.value === 'true';
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(gtmData);
  }

  acceptRequiredCookies() {
    this.element.querySelectorAll('[data-cookie-dialog-option]').forEach(el => {
      el.setAttribute('aria-checked', el.getAttribute('aria-readonly') === 'true' ? 'true' : 'false');
    });

    this.save();
  }

  acceptAllCookies() {
    this.element.querySelectorAll('[data-cookie-dialog-option]').forEach(el => {
      if (el.getAttribute('aria-readonly') !== 'true') {
        el.setAttribute('aria-checked', 'true');
      }
    });

    this.save();
  }
}

export default CookieDialog;
export {
  getCookieValue,
  setCookieValue
};
